import type { VoterFilter, CanvasserType, QuestionAnswersFilter, ProspectBookQuery, Segment, TelemetryRunListParams } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { selectedCampaignKey } from './data';
import { segmentKey } from './voterSegmentKey';
import type { VoterPlaceSearch } from './voterSegmentKey';
import type { getApiClient } from '@signalvote/api-client';

/** `GET /api/admin/assistant-telemetry/overview` takes the run list's date and campaign filters. */
export type TelemetryOverviewParams = Pick<TelemetryRunListParams, 'from' | 'to' | 'campaignId'>;

export interface CanvasserAnalyticsFilters {
  type?: CanvasserType;
  vendorId?: string;
  teamId?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * A Prospects book read's filters as its key holds them: every one present, a missing one read as its default, so
 * `{}` and `{ activity: null, search: '' }` are the same read. `bookQueryOfKey` reads it back out of a key.
 */
export function bookKeyQuery(query: ProspectBookQuery): Required<ProspectBookQuery> {
  return {
    activity: query.activity ?? null,
    match: query.match ?? null,
    sheetId: query.sheetId ?? null,
    search: query.search ?? '',
    listId: query.listId ?? null,
    state: query.state ?? null,
    city: query.city ?? '',
    zip: query.zip ?? '',
    minAsk: query.minAsk ?? null,
    maxAsk: query.maxAsk ?? null,
    timeZone: query.timeZone ?? null,
    sort: query.sort ?? null,
    dir: query.dir ?? null,
  };
}

/** The filters a Prospects book key was built from, or null when the key isn't one book's read. */
export function bookQueryOfKey(key: readonly unknown[]): Required<ProspectBookQuery> | null {
  return key[3] === 'book' && typeof key[4] === 'object' && key[4] !== null ? (key[4] as Required<ProspectBookQuery>) : null;
}

// Campaign scope helper - reads current campaignId for cache isolation
function cid() {
  return useCampaignStore.getState().selectedCampaign?.id ?? '';
}

// Filter types for query keys
export interface VoterListParams extends VoterFilter {
  page?: number;
  limit?: number;
}

export type TaskListParams = NonNullable<Parameters<ReturnType<typeof getApiClient>['tasks']['getTasks']>[0]>;

/**
 * The keys of the finance reads built on `defineQuery`, without the campaign id the layer puts at
 * their head. The definition and `queryKeys` both build from these, so an invalidation names the
 * key the read caches under.
 */
export const financeReadKeys = {
  health: () => ['finance', 'health'] as const,
  obligations: () => ['finance', 'obligations'] as const,
  earliestStagedDate: () => ['finance', 'staged', 'earliest-date'] as const,
  closeCurrent: () => ['finance', 'close', 'current'] as const,
  dashboardSummary: (window: { from: string; to: string }) =>
    ['finance', 'dashboard', 'summary', window.from, window.to] as const,
  dashboardLastFiledCash: () => ['finance', 'dashboard', 'last-filed-cash'] as const,
  dashboardRoster: () => ['finance', 'dashboard', 'roster'] as const,
  dashboardAttention: () => ['finance', 'dashboard', 'attention'] as const,
};

/**
 * Every finance query, for a write that can change any of them. A prefix key cannot do this: the
 * `defineQuery` reads hold the campaign id ahead of `'finance'`.
 */
export const financeQueries = {
  predicate: (query: { queryKey: readonly unknown[] }) =>
    query.queryKey[0] === 'finance' || query.queryKey[1] === 'finance',
};

export const queryKeys = {
  // Voters (campaign-scoped)

  // Volunteers (mixed scope)

  // Analytics (campaign-scoped)
  analytics: {
    all: ['analytics'] as const,
    questionAnswers: (questionId: string, filter: QuestionAnswersFilter) =>
      ['analytics', cid(), 'questionAnswers', questionId, filter] as const,
    attentionNeeded: (filters?: CanvasserAnalyticsFilters, includeDismissed?: boolean) =>
      ['analytics', cid(), 'attentionNeeded', filters, { includeDismissed }] as const,
  },

  // Tags (campaign-scoped)

  // Walkbooks (campaign-scoped)
  walkbooks: {
    all: ['walkbooks'] as const,
    list: (params?: { status?: string; assignedToMe?: boolean; includePolygon?: string }) =>
      ['walkbooks', cid(), 'list', params] as const,
    detail: (id: string) => ['walkbooks', cid(), 'detail', id] as const,
    stats: (id: string | undefined) => ['walkbooks', cid(), 'stats', id] as const,
    houses: (id: string | undefined) => ['walkbooks', cid(), 'houses', id] as const,
    contactRates: () => ['walkbooks', cid(), 'contactRates'] as const,
  },

  // Tenant Campaigns (subsidiary-scoped)
  tenantCampaigns: {
    all: ['tenant-campaigns'] as const,
    my: () => ['tenant-campaigns', 'my'] as const,
    expressConsultant: (id: string) => ['tenant-campaigns', 'express-consultant', id] as const,
  },

  // Vendors (campaign-scoped)
  vendors: {
    all: ['vendors'] as const,
    list: () => ['vendors', cid(), 'list'] as const,
  },

  // Calendar (user-scoped, events are campaign-scoped)
  calendar: {
    all: ['calendar'] as const,
    calendars: (connectionId: string) => ['calendar', 'calendars', connectionId] as const,
    campaignSettings: (campaignId: string) => ['calendar', 'campaign-settings', campaignId] as const,
  },

  // Finance (campaign-scoped for settings, subsidiary-scoped for profiles)
  finance: {
    // ADR-076 (#627) — the per-subsidiary won/lost admin view (GET /api/finance/election-outcomes).
    // Subsidiary-scoped (NO cid()): the read spans every campaign in the subsidiary tenant DB, so
    // it's the same data whatever campaign is selected.
    electionOutcomes: () => ['finance', 'election-outcomes'] as const,
    // USI-007 (ADR-011 § Structural commitment #10): unified Sort Triage
    // Inbox aggregator. Replaces the per-source aggregation the deleted
    // phase-state endpoint did. cid()-scoped so cross-campaign cache
    // stays isolated.
    // Both sort-inbox reads (the display ledger and the NeedsAction feed), for the selected campaign.
    sortInboxAll: () => selectedCampaignKey(['finance', 'sort-inbox']),
    // CAP-009: on-demand presigned URL for a captured check image, fetched
    // when a contribution ledger row expands. cid()-scoped.
    checkImage: (contributionId: string) =>
      ['finance', 'check-image', cid(), contributionId] as const,
    reports: {
      // Mississippi in-app cover review: the derived cover values, the treasurer's stored corrections,
      // and the values the cover will file. cid()-scoped so cross-campaign cache stays isolated.
      mississippiCover: (reportId: string) =>
        ['finance', 'reports', 'mississippi-cover', cid(), reportId] as const,
    },
    health: () => selectedCampaignKey(financeReadKeys.health()),
    // GitHub #164 — VA §24.2-947.11 pre-session report preview (GET
    // /obligations/{id}/pre-session-preview). cid()-scoped so cross-campaign
    // cache stays isolated; keyed by obligationId, staleTime 0 so each modal
    // open + confirm-session-date re-scope refetches.
    preSessionPreview: (obligationId: string) =>
      ['finance', 'obligations', 'pre-session-preview', cid(), obligationId] as const,
    rulesets: {
      reportTypes: (code: string) => ['finance', 'rulesets', 'report-types', code] as const,
    },
    // ADR-017: de-mocked dashboard hero. Summary keys on the resolved [from, to]
    // window so each period selection caches independently; last-filed cash is a
    // single per-campaign figure. cid()-scoped so cross-campaign cache stays isolated.
    dashboard: {
      summary: (from: string, to: string) => selectedCampaignKey(financeReadKeys.dashboardSummary({ from, to })),
      lastFiledCash: () => selectedCampaignKey(financeReadKeys.dashboardLastFiledCash()),
      roster: () => selectedCampaignKey(financeReadKeys.dashboardRoster()),
      attention: () => selectedCampaignKey(financeReadKeys.dashboardAttention()),
    },
    // The live-only Stripe panels (never persisted, ADR-011): the connected account's balance, its
    // payout schedule, and its payouts (paged via Stripe's starting_after cursor). The DB-sourced
    // payments reads are defined reads in useStripeConnectPayments.
    payments: {
      balance: () => ['finance', 'payments', 'balance', cid()] as const,
      payoutSchedule: () =>
        ['finance', 'payments', 'payout-schedule', cid()] as const,
      payouts: () => ['finance', 'payments', 'payouts', cid()] as const,
    },
  },


  // Billing (campaign-scoped for status, subsidiary-scoped for config)

  // Onboarding (campaign-scoped — menu + status read from the manager's real campaign)
  onboarding: {
    all: ['onboarding'] as const,
    menu: () => ['onboarding', cid(), 'menu'] as const,
    status: () => ['onboarding', cid(), 'status'] as const,
  },

  // Work Orders (campaign-scoped — internal operator surface for vendor text sends)
  workOrders: {
    all: ['work-orders'] as const,
    detail: (id: string) => ['work-orders', cid(), 'detail', id] as const,
    // Under detail, so every edit that invalidates the order also reprices its checkout.
    checkoutPreview: (id: string) => ['work-orders', cid(), 'detail', id, 'checkout-preview'] as const,
    list: (params?: { page?: number; pageSize?: number; status?: string }) =>
      ['work-orders', cid(), 'list', params ?? {}] as const,
    // The raised invoice's client secret, held so paying again reuses that invoice. Outside 'work-orders'
    // so a broad invalidation never drops it.
    checkout: (id: string) => ['work-order-checkout', cid(), id] as const,
    // Subsidiary-wide (every campaign), so deliberately not cid()-scoped.
    voterDataQueue: () => ['work-orders', 'voter-data-queue'] as const,
  },

  // Public donor donation page (UNAUTHENTICATED). Deliberately NOT cid()-scoped:
  // there is no campaign context on a public route — the subsidiary + slug in the
  // URL fully identify the page. No auth/tenant headers are ever attached.
  // A Text send's status link (UNAUTHENTICATED): the token alone identifies the send, so not cid()-scoped.
  textSendStatus: {
    byToken: (token: string) => ['textSendStatus', token] as const,
  },

  publicDonation: {
    page: (subsidiary: string, slug: string) =>
      ['publicDonation', subsidiary, slug] as const,
  },

  // Messaging Profile (campaign-scoped — 1:1 with the selected campaign)
  messagingProfile: {
    committee: () => ['messagingProfile', cid(), 'committee'] as const,
    // Mutation keys: which door last saved the facts tells an untick from an edit.
    committeeSaves: () => ['messagingProfile', cid(), 'committee', 'save'] as const,
    committeeSave: (door: 'facts' | 'signature') => ['messagingProfile', cid(), 'committee', 'save', door] as const,
  },

  // Voter data segment builder — live L2 counts (campaign-gated, paywall-exempt)
  voterData: {
    all: ['voterData'] as const,
    catalog: (state?: string) => ['voterData', cid(), 'catalog', state ?? null] as const,
    history: () => ['voterData', cid(), 'counts'] as const,
    counts: (segment: Segment | null) => ['voterData', cid(), 'counts', segment && segmentKey(segment)] as const,
    voteHistory: () => ['voterData', cid(), 'voteCounts'] as const,
    places: (search: VoterPlaceSearch | null) => ['voterData', cid(), 'places', search] as const,
    raceMatch: (state: string, race: string) => ['voterData', cid(), 'raceMatch', state, race] as const,
    voteCounts: (without: Segment | null, fieldId: string) =>
      ['voterData', cid(), 'voteCounts', without && segmentKey(without), fieldId] as const,
  },

  // Voter Import (mixed scope - history is campaign-scoped, records are job-scoped)
  voterImport: {
    stagingTable: (stagingTableName: string) =>
      ['voterImport', cid(), 'stagingTable', stagingTableName] as const,
  },

  // Fundraising / Call Time (campaign-scoped). cid()-scoped so a campaign switch never serves the prior campaign's cache.
  fundraising: {
    all: ['fundraising'] as const,
    callSheets: () => ['fundraising', cid(), 'call-sheets'] as const,
    // A book in its saved order, or in an order someone is only viewing it in — each its own read.
    callSheet: (id: string, sort: string | null = null, dir: string | null = null) =>
      ['fundraising', cid(), 'call-sheets', id, sort ?? 'saved-order', dir ?? 'saved-dir'] as const,
    enrichmentRun: () => ['fundraising', cid(), 'enrichment-run'] as const,
    prospectLists: () => ['fundraising', cid(), 'prospect-lists'] as const,
    // Prefix of every Prospect list read, so a detail edit can patch and refresh all of them.
    prospects: () => ['fundraising', cid(), 'prospects'] as const,
    // Prefix of every Prospects book read, whatever its filters — the one read a small change must never re-ask
    // (it is the whole campaign); a change patches its rows instead.
    prospectBooks: () => ['fundraising', cid(), 'prospects', 'book'] as const,
    // Every filter the book reads is in its key, each defaulting the same whether given or left out, so two
    // reads of the same book share one entry.
    prospectBook: (query: ProspectBookQuery) =>
      ['fundraising', cid(), 'prospects', 'book', bookKeyQuery(query)] as const,
    // The book's figures without its rows. Under the Prospects prefix, so a change to a person re-asks it.
    prospectBookSummary: (query: ProspectBookQuery, withIds: boolean) =>
      ['fundraising', cid(), 'prospects', 'summary', bookKeyQuery(query), withIds] as const,
    // One Prospect's detail page. Under the Prospects prefix, so any change to a person, a verdict or a
    // call book re-asks it.
    prospectDetail: (id: string) => ['fundraising', cid(), 'prospects', 'detail', id] as const,
    // Match review's read: everyone with a candidate waiting, with the candidates. Under the Prospects prefix, so
    // any change to a person or a verdict re-asks it.
    possibleMatches: () => ['fundraising', cid(), 'prospects', 'possible-matches'] as const,
    // Under the Prospects prefix, so any change to a person or a verdict re-asks it.
    matchConfirmPreview: (prospectId: string, candidates: string[]) =>
      ['fundraising', cid(), 'prospects', 'match-preview', prospectId, ...candidates] as const,
    prospectListUndoPreview: (id: string) => ['fundraising', cid(), 'prospect-lists', id, 'undo-preview'] as const,
  },

  // Platform-admin Consultant management. Subsidiary-wide: users and their Express-link campaigns span every campaign.
  consultants: {
    all: ['consultants'] as const,
    list: () => ['consultants', 'list'] as const,
    search: (query: string) => ['consultants', 'search', query] as const,
  },

  // One Express link: who sent it and what a text costs through it (UNAUTHENTICATED, subsidiary-wide), and the campaigns the
  // signed-in person can send from through it.
  expressLink: {
    detail: (code: string) => ['express-link', code, 'detail'] as const,
    // Per person: a shared phone signs one out and another in without reloading the page.
    campaigns: (code: string, userId: string | undefined) => ['express-link', code, 'campaigns', userId] as const,
  },

  // The campaign's Race and its saved Vote Goals; the Race search is the public catalog, shared by every campaign.
  racePicture: {
    campaignRace: () => ['race-picture', cid(), 'campaign-race'] as const,
    search: (state: string, phrase: string) => ['race-picture', 'search', state, phrase] as const,
    race: (raceId: string) => ['race-picture', 'race', raceId] as const,
  },
} as const;
